class SpeechToTextService {
  constructor() {
    this.recognition = null;
    this.isListening = false;
    this.onResultCallback = null;
    this.onEndCallback = null;
    this.onErrorCallback = null;
    this.finalTranscript = '';
    this.interimTranscript = '';
  }

  // Check if browser supports speech recognition
  isSupported() {
    return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
  }

  // Initialize the speech recognition
  initialize() {
    if (!this.isSupported()) {
      throw new Error('Speech recognition is not supported in this browser');
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    this.recognition = new SpeechRecognition();
    
    // Configure recognition settings
    this.recognition.continuous = true; // Keep listening until stopped
    this.recognition.interimResults = true; // Show partial results
    this.recognition.lang = 'en-US';
    this.recognition.maxAlternatives = 1;
    
    // Mobile-specific optimizations
    this.isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (this.isMobile) {
      console.log('📱 Mobile device detected, applying optimizations');
      // On mobile, use shorter continuous listening to avoid timeouts
      this.recognition.continuous = false;
      this.recognition.interimResults = true; // Keep interim results for mobile
      
      // Restart recognition if it stops on mobile (common issue)
      this.autoRestart = true;
    }

    this.setupEventListeners();
  }

  setupEventListeners() {
    // Handle speech recognition results
    this.recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';

      // Process all results
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      // Update transcripts
      this.finalTranscript += finalTranscript;
      this.interimTranscript = interimTranscript;

      // Notify listeners
      if (this.onResultCallback) {
        this.onResultCallback({
          final: this.finalTranscript,
          interim: this.interimTranscript,
          combined: this.finalTranscript + this.interimTranscript
        });
      }
    };

    // Handle recognition start
    this.recognition.onstart = () => {
      this.isListening = true;
      console.log('🎤 Speech recognition started');
    };

    // Handle recognition end
    this.recognition.onend = () => {
      console.log('🛑 Speech recognition ended');
      
      // Auto-restart on mobile if still supposed to be listening
      if (this.autoRestart && this.isListening && this.finalTranscript.length === 0) {
        console.log('🔄 Auto-restarting speech recognition on mobile');
        setTimeout(() => {
          if (this.isListening) {
            try {
              this.recognition.start();
            } catch (error) {
              console.warn('Auto-restart failed:', error);
              this.isListening = false;
              if (this.onEndCallback) {
                this.onEndCallback(this.finalTranscript);
              }
            }
          }
        }, 100);
        return;
      }
      
      this.isListening = false;
      
      if (this.onEndCallback) {
        this.onEndCallback(this.finalTranscript);
      }
    };

    // Handle errors
    this.recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      this.isListening = false;
      
      let errorMessage = 'Speech recognition error';
      switch (event.error) {
        case 'no-speech':
          errorMessage = this.isMobile ? 
            'No speech detected. On mobile, speak clearly and try again.' : 
            'No speech detected. Please try again.';
          break;
        case 'audio-capture':
          errorMessage = this.isMobile ? 
            'Microphone not found. Please check permissions in browser settings.' : 
            'No microphone found. Please check your microphone.';
          break;
        case 'not-allowed':
          errorMessage = this.isMobile ? 
            'Microphone blocked. Enable microphone in browser settings and reload page.' : 
            'Microphone access denied. Please allow microphone access.';
          break;
        case 'network':
          errorMessage = 'Network error. Please check your connection.';
          break;
        case 'service-not-allowed':
          errorMessage = this.isMobile ? 
            'Speech service unavailable. Try using Chrome or Safari.' : 
            'Speech recognition service not available.';
          break;
        default:
          errorMessage = `Speech recognition error: ${event.error}`;
      }

      if (this.onErrorCallback) {
        this.onErrorCallback(errorMessage);
      }
    };
  }

  // Check microphone permissions
  async checkMicrophonePermission() {
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const permission = await navigator.permissions.query({ name: 'microphone' });
        return permission.state;
      } catch (error) {
        console.warn('Could not check microphone permission:', error);
        return 'unknown';
      }
    }
    return 'unknown';
  }

  // Start listening
  async startListening() {
    if (!this.recognition) {
      this.initialize();
    }

    if (this.isListening) {
      console.warn('Already listening');
      return;
    }

    // Check permissions on mobile
    if (this.isMobile) {
      const permission = await this.checkMicrophonePermission();
      console.log('📱 Mobile microphone permission:', permission);
      
      if (permission === 'denied') {
        if (this.onErrorCallback) {
          this.onErrorCallback('Microphone blocked. Enable microphone in browser settings and reload page.');
        }
        return;
      }
    }

    // Reset transcripts
    this.finalTranscript = '';
    this.interimTranscript = '';

    try {
      // Add a small delay on mobile to improve reliability
      if (this.isMobile) {
        console.log('📱 Starting speech recognition with mobile optimizations');
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      this.recognition.start();
    } catch (error) {
      console.error('Error starting speech recognition:', error);
      if (this.onErrorCallback) {
        this.onErrorCallback(
          this.isMobile 
            ? 'Could not start speech recognition. Try refreshing and allow microphone access.'
            : 'Failed to start speech recognition'
        );
      }
    }
  }

  // Stop listening
  stopListening() {
    if (this.recognition && this.isListening) {
      this.recognition.stop();
    }
  }

  // Set callback for results
  onResult(callback) {
    this.onResultCallback = callback;
  }

  // Set callback for end
  onEnd(callback) {
    this.onEndCallback = callback;
  }

  // Set callback for errors
  onError(callback) {
    this.onErrorCallback = callback;
  }

  // Get current listening state
  getIsListening() {
    return this.isListening;
  }

  // Clean up
  destroy() {
    if (this.recognition) {
      this.recognition.stop();
      this.recognition = null;
    }
    this.onResultCallback = null;
    this.onEndCallback = null;
    this.onErrorCallback = null;
  }
}

// Create and export a singleton instance
const speechToTextService = new SpeechToTextService();
export default speechToTextService;